<template>
  <div class="fairstone-plans-list">
    <portal to="breadcrumb">
      <div class="plans-filter d-flex align-items-center justify-content-sm-between">
        <div class="plans-list-breadcrumb">
          <bread-crumb
            :activeModule="$route.meta.moduleLabel"
            :activePage="$route.meta.label"
            :activePageUrl="$route.path"
          />
        </div>
        <b-form-group
          id="siteId"
          label="Select website"
          label-for="siteId"
          :label-class="['form-label', 'required']"
          style="width: 350px;"
        >
          <b-dropdown
            id="dropdown-form"
            :text="formData.siteId == '' ? 'Select site' : selectedSite?.storeName"
            ref="dropdown"
            block
            menu-class="w-100"
            no-caret
            lazy
            variant="brand-dropdown"
            @shown="focusSearchInput"
          >
            <b-dropdown-form @submit.prevent>
              <div class="form-group">
                <b-form-input
                  type="search"
                  class="nosubmit w-100"
                  placeholder="Search"
                  ref="siteSearchInput"
                  v-model="siteSearchText"
                />
              </div>
              <div class="scrollable">
                <b-dropdown-item
                  v-for="(site, index) in filteredSiteOptions"
                  :key="index"
                  @click="onSiteSelect(site)"
                  :active="formData.siteId == site.value"
                  >{{ site.text }}
                </b-dropdown-item>
              </div>
            </b-dropdown-form>
          </b-dropdown>
        </b-form-group>
          
      </div>
    </portal>
    <b-card class="mt-3 admin-grid">
      <div class="row d-md-flex align-items-center mb-0 mb-md-3">
        <div class="col-xl-4 col-md-6 col-sm-12 pl-xxl-0 pr-md-0 mb-2 mb-md-2 mb-xl-0">
          <input
            type="search"
            v-model="planSearch"
            placeholder="Search"
            class="nosubmit w-100"
          />
        </div>
        <div class="col-xl-6 col-md-6 col-sm-12 pl-xxl-0 mb-2 mb-md-0"></div>
        <div class="col-xl-2 col-md-6 col-sm-12 pl-xxl-0 mb-2 mb-md-0">
          <b-dropdown
            id="status-dropdown"
            text="Change Status"
            ref="statusDropdown"
            block
            no-caret
            variant="brand-dropdown"
            menu-class="w-100 scrollable"
            :disabled="planIds.length == 0"
          >
            <b-dropdown-item
              v-for="(option, index) in statusOptions"
              :key="index"
              :active="selectedStatus == option.value"
              @click="setSelectedStatus(option)"
            >
              {{ option.text }}</b-dropdown-item
            >
            <b-dropdown-item class="dropdown-footer">
              <b-button
                variant="brand-secondary"
                size="sm"
                @click="handleChangeStatus"
                >Apply</b-button
              >
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
      <b-table
        hover
        responsive
        :items="items"
        :fields="fields"
        :per-page="perPage"
        :current-page="currentPage"
        tbody-tr-class="text-nowrap"
        ref="plansTable"
        show-empty
        :empty-text="$t('general.no-data-msg')"
        :filter="filter"
        :filter-included-fields="filterOn"
        @filtered="onFiltered"
        :filter-debounce="300"
        :filter-function="filterTable"
        v-model="displayedRows"
      >
        <template #head(checkbox)>
          <b-form-checkbox
            v-model="allSelected"
            @change="toggleAll"
            class="align-items-start"
          >
          </b-form-checkbox>
        </template>
        <template #cell(checkbox)="data">
          <b-form-checkbox
            class="d-block"
            :id="'checkbox-' + data.item.productId"
            :value="data.item.productId"
            v-model="planIds"
          />
        </template>

        <template #cell(finanPlanDefault)="data">
          <div @click="editPlan(data.item)" role="button">
            <span v-if="data.item.finanPlanDefault" role="button">{{ data.item.finanPlanDefault ? 'Yes' : '' }}</span>
          </div>
        </template>

        <template #cell(isEnabled)="data">
          <div @click="editPlan(data.item)" role="button">
            <span
              class="tag-status"
              :class="{
                inactive: !data.item.isEnabled,
                active: data.item.isEnabled,
              }"
              role="button"
              >{{ data.item.isEnabled ? 'Enabled' : 'Disabled' }}</span
            >
          </div>
        </template>

        <template #cell(productId)="data">
          <div
            @click="editPlan(data.item)"
            class="editable-value"
            v-b-tooltip.hover="data.item.productId"
          >
            {{ data.item.productId }}
          </div>
        </template>

        <template #cell(finanPaymentType)="data">
          <div
            @click="editPlan(data.item)"
            class="editable-value"
            v-b-tooltip.hover="data.item.finanPaymentType"
          >
            {{ data.item.finanPaymentType }}
          </div>
        </template>

        <template #cell(finanStartDate)="data">
          <div
            @click="editPlan(data.item)"
            class="editable-value"
            v-b-tooltip.hover="data.item.finanStartDate"
          >
            {{ data.item.finanStartDate }}
          </div>
        </template>

        <template #cell(finanEndDate)="data">
          <div
            @click="editPlan(data.item)"
            class="editable-value"
            v-b-tooltip.hover="data.item.finanEndDate"
          >
            {{ data.item.finanEndDate }}
          </div>
        </template>

        <template #cell()="data">
          <div @click="editPlan(data.item)" class="editable-value">
            {{ data.value }}
          </div>
        </template>
      </b-table>
      <table-pagination
        class="mt-3"
        :fromLimit="fromLimit | addLeadingZero"
        :toLimit="toLimit | addLeadingZero"
        :totalRecords="totalRecords | addLeadingZero"
        :currentPage="currentPage"
        :totalPages="totalPages"
        @paginate="paginate"
        recordsTitle="plans"
      />
    </b-card>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import config from "../../../../../config";
import { includes, forEach, map, ceil, uniq, upperFirst } from "lodash";
import TablePagination from "../../../../components/TablePagination.vue";
import BreadCrumb from "../../../../components/BreadCrumb.vue";

const { tableHeaders, fairstonePlansManager, enabledOptions } = config;

const { modalSetting } = fairstonePlansManager;

const createDefault = function () {
  return {
    siteId: null
  };
};
export default {
  components: { TablePagination, BreadCrumb },
  name: "FairstonePlansIndex",
  data() {
    return {
      formData: createDefault(),
      siteSearchText: "",
      selectedSite: {storeId:null, storeName:null},
      showFilter: false,
      isStoreFilterVisible: false,
      currentPage: 1,
      totalRecords: 0,
      totalPages: 0,
      perPage: 20,
      siteOptions: [],
      fields: tableHeaders["fairstone-plans-list"],
      allSelected: false,
      indeterminate: false,
      isStatusItemClicked: false,
      planIds: [],
      displayedRows: [],
      selectedSites: [],
      statusOptions: enabledOptions,
      selectedStatus: 0,

      planSearch: "",

      searchSiteText: "",
      storeFilterSelection: [],
      statusFilterSelection: [],
      filterOn: [],
      filter: {
        planSearch: "",
        productId: "",
        finanPaymentType: "",
        finanStartDate: "",
        finanEndDate: "",
        isEnabled: [],
      },
    };
  },
  computed: {
    ...mapGetters("fairstonePlansManager", ["gridList", "tblLoader"]),
    ...mapGetters("siteManager", ["storeList"]),
    planData() {
      return this.gridList;
    },
    items() {
      return this.planData;
    },
    fromLimit() {
      let response = this.getPaginationResultLimit(
        this.currentPage,
        this.perPage,
        this.totalRecords
      );
      return response.fromlimit;
    },
    toLimit() {
      let response = this.getPaginationResultLimit(
        this.currentPage,
        this.perPage,
        this.totalRecords
      );
      return response.tolimit;
    },
    filteredSiteOptions() {
      if (!this.siteSearchText) return this.siteOptions;
      const search = this.siteSearchText.toLowerCase();
      return this.siteOptions.filter(site => {
        if (site.text.toLowerCase().includes(search)) { return true; } 
        return false;
      });
    }
  },
  watch: {
    "formData.siteId": async function (newVal) {

      this.selectedSite = this.storeList.find((site) => site.storeId == newVal);
      
      this.setItem({ resource: "isLoading", payload: true });

      this.setItem({ resource: "isLoading", payload: false });
    },
    planSearch(newValue) {
      let _filterOn = this.filterOn;
      let index = _filterOn.indexOf("planSearch");

      if (newValue != "") {
        if (index == -1) {
          _filterOn.push("planSearch");
        }
      } else {
        if (index > -1) {
          _filterOn.splice(index, 1);
        }
      }
      this.filterOn = _filterOn;
      this.filter["planSearch"] = newValue;
    },
    async gridList() {
      await this.resetPageFilters(1);
      let options = [];
      forEach(this.storeList, (site) => {
        let obj = { text: site.storeName, value: site.storeId };
        options.push(obj);
      });
      this.siteOptions = options;
    },
    async currentPage(newValue) {
      await this.resetPageFilters(newValue);
    },
    planIds(newValue) {
      if (
        this.displayedRows.length &&
        this.displayedRows.length == newValue.length
      ) {
        this.allSelected = true;
        this.indeterminate = false;
      } else {
        this.allSelected = false;
        this.indeterminate = true;
      }
    },
  },
  methods: {
    setSelectedStatus(option) {
      this.isStatusItemClicked = true;
      this.selectedStatus = option.value;
    },
    ...mapMutations({
      generalSetItem: "setItem",
    }),
    ...mapActions("fairstonePlansManager", [
      "getAll",
      "updateStatus",
    ]),
    ...mapMutations("fairstonePlansManager", ["setItem", "setSelectedSiteId"]),
    ...mapActions("siteManager", ["getStoreList"]),
    toggleAll(checked) {
      this.indeterminate = false;
      let selectedPlans = [];
      if (checked) {
        forEach(this.displayedRows, (row) => {
          selectedPlans.push(row.productId);

        });
      }
      this.planIds = selectedPlans;
    },
    async onSiteSelect(site) {
      this.formData.siteId = site.value;
      this.setSelectedSiteId((site.value));
      if (this.$route.params.siteId !== site.value) {
        try {
          await this.$router.replace({
            name: "fairstone-plans-list",
            params: { siteId: site.value }
          });
        } catch (err) {
          if (!err.message.includes("Avoided redundant navigation")) {
            console.error(err);
          }
        }
      }
      await this.loadFairstonPlansListing(this.formData.siteId);
    },
    focusSearchInput() {
      this.$nextTick(() => {
        this.$refs.siteSearchInput?.focus();
      });
    },
    async handleChangeStatus() {
      let modalTitle = "";
      let modalDescription = "";

      if (this.selectedStatus == 0) {
        modalTitle = modalSetting.inActivePlan.title;
        modalDescription = modalSetting.inActivePlan.description;
      } else {
        modalTitle = modalSetting.activatePlan.title;
        modalDescription = modalSetting.activatePlan.description;
      }

      const h = this.$createElement;

      const titleVNode = h("h5", {
        class: ["modal-title font-weight-bold"],
        domProps: { innerHTML: modalTitle },
      });

      this.$bvModal
        .msgBoxConfirm(modalDescription, {
          title: [titleVNode],
          size: "sm",
          buttonSize: "md",
          okVariant: "brand-primary w-50",
          cancelVariant: "brand-secondary border-0 w-50",
          okTitle: "Save",
          cancelTitle: "Cancel",
          headerClass:
            "justify-content-center p-3 border-bottom-0 font-weight-bolder",
          footerClass: "p-3 border-top-0 flex-nowrap",
          hideHeaderClose: true,
          centered: true,
          noCloseOnBackdrop: true,
          noCloseOnEsc: true,
        })
        .then(async (value) => {
          if (value) {
            this.generalSetItem({ resource: "isLoading", payload: true });
            let data = {
              siteId: this.formData.siteId,
              productIds: this.planIds,
              isEnabled: this.selectedStatus,
            };
            let response = await this.updateStatus(data);
            if (response.error) {
              this.$bvToast.toast(`${response.error}`, {
                title: "Error",
                variant: "danger",
                autoHideDelay: 5000,
                solid: true
              });
            } else {
              this.$bvToast.toast("Status updated.", {
                title: "Success",
                variant: "success",
                autoHideDelay: 5000,
                solid: true,
              });
            }
            await this.getAll({ siteId: this.formData.siteId });
            this.generalSetItem({ resource: "isLoading", payload: false });
          }
        })
        .catch((error) => {
          this.generalSetItem({ resource: "isLoading", payload: false });
        });
    },
    async editPlan(item) {
      this.$router.push({ name: "edit-fairstone-plans", params: { siteId: item.siteId, productId: item.productId } });
    },
    async resetPageFilters(currentPage) {
      this.currentPage = currentPage;
      this.planIds = [];
      this.siteOptions = [];
      this.selectedSites = [];
      this.allSelected = this.indeterminate = false;
      await this.getStoreList({ userId: this.loggedUserId });
    },
    paginate(eventType) {
      if (eventType == "next") {
        this.currentPage = this.currentPage + 1;
      } else {
        this.currentPage = this.currentPage - 1;
      }
    },
    filterTable(row, filter) {
      let filterCondition = {};
      if (this.filterOn.length) {
        this.filterOn.forEach((item) => {
          if (filter[item].length) {
            filterCondition[item] = true;
            if (
              item == "productId" ||
              item == "finanPaymentType" ||
              item == "finanStartDate" ||
              item == "finanEndDate"
            ) {
              if (!row[item].toLowerCase().includes(filter[item])) {
                filterCondition[item] = false;
              }
            } else if (item == "isEnabled") {
              if (!filter[item].includes(row[item])) {
                filterCondition[item] = false;
              }
            } else if (item == "planSearch") {
              let productIdMatched = row["productId"]
                .toLowerCase()
                .includes(filter[item].toLowerCase());
              let finanPaymentTypeMatched = row["finanPaymentType"]
                .toLowerCase()
                .includes(filter[item].toLowerCase());

              filterCondition[item] =
                productIdMatched || finanPaymentTypeMatched;
            }
          }
        });
      }

      const areTrue = Object.values(filterCondition).every(
        (value) => value === true
      );
      return areTrue;
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.currentPage = 1;
      this.totalRecords = filteredItems.length;
      this.totalPages = ceil(filteredItems.length / this.perPage);
    },
    resetGridFilters() {
      this.planSearch = "";
      this.searchSiteText = "";
      this.storeFilterSelection = [];
      this.statusFilterSelection = [];
      this.filterOn = [];
      this.filter = {
        planSearch: "",
        productId: "",
        finanPaymentType: "",
        finanStartDate: "",
        finanEndDate: "",
        isEnabled: [],
      };
    },
    closeFilter() {
      this.showFilter = false;
      this.toggleSiteDropdownFilter("close");
    },
    async loadFairstonPlansListing(siteId) {
      this.setItem({ resource: "searchQuery", payload: "" });
      this.generalSetItem({ resource: "isLoading", payload: true });
      this.resetGridFilters();
      
      

      await this.getAll({ siteId: siteId })
      .then(() => {
        this.generalSetItem({ resource: "isLoading", payload: false });
      })
      .catch(() => {
        this.generalSetItem({ resource: "isLoading", payload: false });
      });
    }
  },
  async created() {
    const storelist = await this.getStoreList({ userId: this.loggedUserId });
    if (!storelist || storelist.length === 0) return;

    const routeSiteId = this.$route.params.siteId || this.$route.query.siteId;
    const defaultSiteId = storelist[0].storeId;
    const initialSiteId = routeSiteId || defaultSiteId;

    this.formData.siteId = initialSiteId;
    this.selectedSite = storelist.find(s => s.storeId === initialSiteId) || storelist[0];
    this.setSelectedSiteId(initialSiteId);

    if (!this.$route.params.siteId && !this.$route.query.siteId) {
      try {
        await this.$router.replace({
          name: "fairstone-plans-list",
          params: { siteId: initialSiteId }
        });
      } catch (err) {
        if (!err.message.includes("Avoided redundant navigation")) {
          console.error(err);
        }
      }
    }
    await this.loadFairstonPlansListing(initialSiteId);
  },
  mounted: async function () {
    this.$root.$on("bv::dropdown::show", (bvEvent) => {
      if (bvEvent.componentId === "site-selection-form") {
        this.isStoreFilterVisible = true;
      }
    });
    this.$root.$on("bv::dropdown::hide", (bvEvent) => {
      if (bvEvent.componentId === "status-dropdown") {
        if (this.isStatusItemClicked) {
          bvEvent.preventDefault();
          this.isStatusItemClicked = false;
        }
      }
      if (bvEvent.componentId === "site-selection-form") {
        this.isStoreFilterVisible = false;
      }
      if (this.isStoreFilterVisible) {
        bvEvent.preventDefault();
      }
    });
  },
  destroyed() {
    this.setItem({ resource: "searchQuery", payload: "" });
    this.resetGridFilters();
  },
};
</script>
